import React, {useEffect, useRef, useState} from 'react';
import {renderToStaticMarkup} from 'react-dom/server';
import {useTranslation} from 'react-i18next';
import relativeTime from 'dayjs/plugin/relativeTime';
import InfiniteScroll from 'react-infinite-scroller';
import config from '../../service/config';
import {Avatar, TscAvatarSize, ControlButton, TscButtonType, EmptyState, Glyphs, MediaCard, TscMediaOrganizationType, Spinner, TscThemeNames, Label} from '@techsmith/tsc-cloud-style-guide';
import {IMediaDetails} from '../../model/mediaDetails';
import dayjs from 'dayjs';
import {ButtonStyledAsAnchor, GridLayoutContainer, LibraryContentContainer, MediaSectionHeader, Subheader, SubheaderControls, SubheaderTitleContainer} from '../util/StyledElements';
import decode from 'entity-decode';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import collectionApi from '../../service/collectionApi';
import {buildMediaQueryParams} from '../util/MediaQueryParamsBuilder';
import Axios, {CancelTokenSource} from 'axios';
import Constants, {CollectionPrivacyTypes, GroupRoleTypes, SortMetric} from '../../constants/Constants';
import {collectionMediaStore, updateCollectionMediaState} from '../../context/collectionMediaProvider';
import {collectionsStore, updateActiveCollectionById} from '../../context/collectionsProvider';
import {ICollectionModel, IMemberDetailsModel, IMemberDetailsWithProfileImageModel} from '../../model/appModel';
import EmptyLibrary from '../../static/svg/EmptyLibrary.svg';
import TimeFormatter from '../../util/TimeFormatter';
import styled from 'styled-components';
import {sortMedia} from '../../util/MediaSorter';
import SortDirectionOptions from '../util/SortDirectionOptions';
import {sortMetricStore} from '../../service/sortMetricProvider';
import MediaProcessingGlyph from '../../static/svg/MediaProcessingGlyph.svg';
import MediaPrivateGlyph from '../../static/svg/MediaPrivateGlyph.svg';
import CollectionActionMenu from './CollectionActionMenu';
import {CollectionMediaRemove} from '../details/CollectionMediaRemove';
import profileApi from '../../service/profileApi';
import {useParams} from 'react-router';
import {batchSelectionStore, clearBatchSelection, updateBatchSelectionState} from '../../context/batchSelectionProvider';
import BatchSelectionIndicator from '../library/BatchSelectionIndicator';
import ShareCollectionModal from '../modals/ShareCollectionModal';

interface IRouterParams {
   id: string;
}

dayjs.extend(relativeTime);

export const SubheaderTitle = styled.h3`
   overflow: hidden;
   text-overflow: ellipsis;
`;

const CollectionMemberAvatars = styled(ButtonStyledAsAnchor)`
   display: flex;
   align-items: center;
   margin-left: 1rem;
   text-decoration: none;

   .member-avatar {
      margin-left: .25rem;
   }
`;

export const PrivateCollectionEmptyStateContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   min-height: 100%;
`;

export const AvatarButtonContainer = styled.div`
   display: grid;
   grid-template-columns: auto auto;
   grid-gap: .25rem;
   flex-shrink: 0;
}`;

const MoreMembersButton = styled(ControlButton)`
   border-radius: 1rem;

   &:focus:after {
      border-radius: 1rem;
   }
`;

const decorateMemberWithProfileImageUrl = (user: IMemberDetailsModel): IMemberDetailsWithProfileImageModel => ({...user, profileImageUrl: profileApi.getProfileImageUrl(user.TechSmithId)});
const collectionMemberRolePriority = (role: GroupRoleTypes): number => role === GroupRoleTypes.manager ? 2 : role === GroupRoleTypes.contributor ? 1 : 0;
const compareMemberRoles = (m1: IMemberDetailsModel, m2: IMemberDetailsModel) => m1.Role === m2.Role ? 0 : collectionMemberRolePriority(m1.Role) > collectionMemberRolePriority(m2.Role) ? -1 : 1;

export const CollectionContentBase: React.FC<ICollectionContentProps & IStateMappedProps> = ({isManagementView, theme, activeCollection, isUpdatingSidebarCollections, collections, collectionMedia, collectionMediaCanLoadMore, collectionMediaCursor, sortMetric, selectedMedia}) => {
   const {id} = useParams<IRouterParams>();
   const {t} = useTranslation();
   const mediaProcessingSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<MediaProcessingGlyph />))}`;
   const mediaPrivateSvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<MediaPrivateGlyph />))}`;
   const [isFetchingMedia, setIsFetchingMedia] = useState(!!(id || activeCollection));
   const getMediaCancellationToken = useRef<CancelTokenSource>(null);
   const [showShareModal, setShowShareModal] = useState(false);
   const [members, setMembers] = useState<IMemberDetailsWithProfileImageModel[]>(activeCollection?.MemberDetails.map(m => decorateMemberWithProfileImageUrl(m)) ?? []);
   const [showCollectionMediaRemove, setShowCollectionMediaRemove] = useState(false);
   const [mediaToRemove, setMediaToRemove] = useState([]);

   const initialize = async () => {
      if (!activeCollection) {
         await updateActiveCollectionById(id);
      }
   };

   useEffect(() => {
      void initialize();
   }, [id, collections]);

   useEffect(() => {
      if (activeCollection) {
         clearBatchSelection();
         void getCollectionMediaDiscardingExistingMedia();
      }
   }, [activeCollection]);

   useEffect(() => {
      if (activeCollection?.MemberDetails) {
         activeCollection?.MemberDetails.sort(compareMemberRoles);
         setMembers(activeCollection?.MemberDetails.map(m => decorateMemberWithProfileImageUrl(m)));
      }
   }, [activeCollection?.MemberDetails?.length]);

   useEffect(() => {
      if (collectionMediaCanLoadMore) {
         void (activeCollection && getCollectionMediaDiscardingExistingMedia());
      } else if (collectionMedia) {
         updateCollectionMediaState({collectionMedia: Object.assign([], sortMedia(collectionMedia, sortMetric, true))});
      }
   }, [sortMetric]);

   const generateGetMediaCancellationToken = () => {
      const currentCancellationToken = getMediaCancellationToken.current;
      if (currentCancellationToken) {
         currentCancellationToken.cancel();
      }

      const newCancellationToken = Axios.CancelToken.source();
      getMediaCancellationToken.current = newCancellationToken;
      return newCancellationToken;
   };

   const getCollectionMediaDiscardingExistingMedia = async () => {
      updateCollectionMediaState({collectionMedia: [], collectionMediaCursor: null});
      // We need to pass the cursor to the get collection method because the state doesn't always update fast enough
      await getCollectionMedia([], null);
   };

   const getCollectionMedia = async (existingMedia: IMediaDetails[], collectionCursor: string) => {
      setIsFetchingMedia(true);

      let newBatch: IMediaDetails[];
      try {
         newBatch = await collectionApi.getMedia(activeCollection?.Id, buildMediaQueryParams(sortMetric, collectionCursor), generateGetMediaCancellationToken());
      } catch (error) {
         if (Axios.isCancel(error)) {
            return;
         }

         throw error;
      }

      if (newBatch?.length) {
         const loadedMedia = existingMedia.concat(newBatch);
         const newLastLoadedMediaHash = newBatch[newBatch.length - 1].Hash;
         updateCollectionMediaState({
            collectionMedia: loadedMedia,
            collectionMediaCursor: newLastLoadedMediaHash,
            collectionMediaCanLoadMore: newBatch.length >= Constants.mediaPaginationBatchSize
         });
      } else {
         updateCollectionMediaState({collectionMediaCanLoadMore: false});
      }

      setIsFetchingMedia(false);
   };

   const selectMedia = (media: IMediaDetails) => {
      const newSelectedMedia: IMediaDetails[] = selectedMedia.slice();
      const mediaIndex: number = newSelectedMedia.findIndex(m => m.Hash === media.Hash);

      if (mediaIndex === -1) {
         newSelectedMedia.push(media);
      } else {
         newSelectedMedia.splice(mediaIndex, 1);
      }

      updateBatchSelectionState({selectedMedia: newSelectedMedia});
   };

   const onRemoveMediaClick = (media: IMediaDetails) => {
      setMediaToRemove([media]);
      setShowCollectionMediaRemove(true);
   };

   const onCloseRemoveMedia = () => {
      setShowCollectionMediaRemove(false);
      setMediaToRemove([]);
   };

   const maxMembersToDisplay = 5;
   const moreMembersCount = members.length - maxMembersToDisplay;
   const membersContent = members && (
      <AvatarButtonContainer>
         <CollectionMemberAvatars onClick={() => setShowShareModal(true)}>
            {members.slice(0, maxMembersToDisplay).map(m => (
               <Avatar
                  key={`member-avatar-${m.TechSmithId}`}
                  size={TscAvatarSize.medium}
                  userDisplayName={m.DisplayName}
                  userAltText={m.EmailAddress}
                  userID={m.TechSmithId}
                  avatarImageUrl={m.profileImageUrl}
                  testId={`collection-member-user-avatar-${m.TechSmithId}`}
                  className="member-avatar"
               />
            ))}
         </CollectionMemberAvatars>
         {moreMembersCount > 0 && (
            <MoreMembersButton
               buttonType={TscButtonType.secondary}
               themeName={theme}
               title={t('collection.modal.members.membersTitle')}
               label={`+${moreMembersCount.toString()}`}
               testId="collection-more-members"
               onClick={() => setShowShareModal(true)}
            />
         )}
      </AvatarButtonContainer>
   );
   const emptyLibrarySvgDataUri = `data:image/svg+xml;base64,${btoa(renderToStaticMarkup(<EmptyLibrary />))}`;
   const collectionHasMedia = collectionMedia?.length > 0;
   const showPrivateCollectionEmptyState = !config.isAdmin && activeCollection?.Privacy === CollectionPrivacyTypes.invite && activeCollection?.Role === GroupRoleTypes.notMember;
   const areItemsSelected = selectedMedia?.length > 0;
   const userCanAdmin = config.user.IsSignedIn && (config.isAdmin || activeCollection?.Role === GroupRoleTypes.manager);

   const userCanRemove = (media: IMediaDetails) => userCanAdmin || (activeCollection?.Role === GroupRoleTypes.contributor && media.OwnerLibrary.User.TechSmithId === config.user.TechSmithId);

   return (
      <>
         {showPrivateCollectionEmptyState && (
            <PrivateCollectionEmptyStateContainer>
               <EmptyState
                  maxWidth="25rem"
                  title={t('collection.privateTitle')}
                  text={t('collection.privateText')}
                  imageSource={emptyLibrarySvgDataUri}
                  themeName={theme}
                  alt={t('collection.privateImageAltText')}
               />
            </PrivateCollectionEmptyStateContainer>
         )}
         {!showPrivateCollectionEmptyState && (
            <InfiniteScroll
               loadMore={() => getCollectionMedia(collectionMedia, collectionMediaCursor)}
               initialLoad={true}
               hasMore={collectionMediaCanLoadMore && !isFetchingMedia}
               useWindow={true}
            >
               <Subheader
                  theme={theme}
                  isVisible={!showPrivateCollectionEmptyState}
               >
                  <SubheaderControls theme={theme}>
                     <SubheaderTitleContainer theme={theme}>
                        {areItemsSelected && <BatchSelectionIndicator />}
                        {!areItemsSelected && (
                           <>
                              <Glyphs.Collection16x16 />
                              <SubheaderTitle className="t-collection-name">
                                 {activeCollection?.Name}
                              </SubheaderTitle>
                              {membersContent}
                           </>
                        )}
                     </SubheaderTitleContainer>
                     {!isUpdatingSidebarCollections && <CollectionActionMenu isManagementView={isManagementView} />}
                  </SubheaderControls>
               </Subheader>
               <LibraryContentContainer data-test-id="collection-content">
                  <MediaSectionHeader theme={theme}>
                     <Label labelText={t('library.media.sectionHeader')} />
                     <SortDirectionOptions />
                  </MediaSectionHeader>
                  <GridLayoutContainer hasMedia={collectionHasMedia}>
                     {collectionHasMedia && collectionMedia.map(media => (
                        <MediaCard
                           key={media.Hash}
                           mediaDate={dayjs(media.AddedToGroupDateTime).fromNow()}
                           mediaDateTitle={dayjs(media.AddedToGroupDateTime).toLocaleString()}
                           mediaDuration={media.IsVideo && media.Duration ? TimeFormatter.formatTime(TimeFormatter.getSecondsFromTimeString(media.Duration)) : ''}
                           mediaUrl={`/${media.Hash}?collection=${id}&sort=${sortMetric}`}
                           ownerAvatarUrl={`${config.environmentData.ProfileApiUrl}/api/v1/profiles/${media.OwnerLibrary.User.TechSmithId}/default/picture`}
                           ownerId={media.OwnerLibrary.User.TechSmithId}
                           ownerName={media.Author}
                           themeName={theme}
                           thumbnailUrl={media.IsReady ? media.ThumbnailUrl ?? mediaPrivateSvgDataUri : mediaProcessingSvgDataUri}
                           title={decode(media.Title)}
                           maxTitleLines={1}
                           viewCount={media.ViewCount}
                           organizationType={TscMediaOrganizationType.collection}
                           hasCaptions={media.HasPublishedCaptions}
                           showDetails={true}
                           testId={`t-media-${media.Hash}`}
                           controls={isManagementView && !areItemsSelected && userCanRemove(media) && (
                              <ControlButton
                                 themeName={theme}
                                 title={t('removeMedia.removeMedia')}
                                 glyph={<Glyphs.CircleRemove16x16 />}
                                 buttonType={TscButtonType.tertiary}
                                 onClick={() => onRemoveMediaClick(media)}
                                 testId={`remove-button-${media.Hash}`}
                              />
                           )}
                           isSelected={userCanAdmin ? selectedMedia.some(m => m.Hash === media.Hash) : null}
                           onSelect={isManagementView && userCanAdmin ? (): void => selectMedia(media) : null}
                        />
                     ))}
                     {!isFetchingMedia && !collectionHasMedia && (
                        <EmptyState
                           maxWidth="25rem"
                           title={t('collection.emptyTitle')}
                           text={t('collection.emptyText')}
                           imageSource={emptyLibrarySvgDataUri}
                           themeName={theme}
                           alt={t('collection.emptyImageAltText')}
                        />
                     )}
                  </GridLayoutContainer>
                  {isFetchingMedia && (
                     <Spinner
                        testId="t-collection-loading"
                        key="spinner"
                        themeName={theme}
                     />
                  )}
               </LibraryContentContainer>
            </InfiniteScroll>
         )}
         {showShareModal && <ShareCollectionModal onClose={() => setShowShareModal(false)} />}
         {showCollectionMediaRemove && (
            <CollectionMediaRemove
               onClose={onCloseRemoveMedia}
               collection={activeCollection}
               media={mediaToRemove}
            />
         )}
      </>
   );
};

export interface ICollectionContentProps {
   isManagementView: boolean;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
   activeCollection: ICollectionModel;
   collections: ICollectionModel[];
   isUpdatingSidebarCollections: boolean;
   collectionMedia: IMediaDetails[];
   collectionMediaCanLoadMore: boolean;
   collectionMediaCursor?: string;
   sortMetric: SortMetric;
   selectedMedia: IMediaDetails[];
}

const mapStatesToProps = (
   {isManagementView}: ICollectionContentProps,
   {theme}: Partial<IStateMappedProps>,
   {activeCollection, collections, isUpdatingSidebarCollections}: Partial<IStateMappedProps>,
   {collectionMedia, collectionMediaCanLoadMore, collectionMediaCursor}: Partial<IStateMappedProps>,
   {sortMetric}: Partial<IStateMappedProps>,
   {selectedMedia}: Partial<IStateMappedProps>
): ICollectionContentProps & IStateMappedProps => ({isManagementView, theme, activeCollection, collections, isUpdatingSidebarCollections, collectionMedia, collectionMediaCanLoadMore, collectionMediaCursor, sortMetric, selectedMedia});

export default withMemoizedContexts(mapStatesToProps, themeStore, collectionsStore, collectionMediaStore, sortMetricStore, batchSelectionStore)(CollectionContentBase);
